// 2026-03-01 (Dave) — Reusable attachment hover preview.
// Extracted from ElementsScreen so it can be reused on the Association
// tab's "Attachments (N)" pill (in ControlsStrip). Renders whatever the
// caller provides (a badge, a button label, etc.) and floats a small
// popover to the LEFT of the trigger with 40 × 40 tiles for each
// attached file — JPEG/PNG show as <img>, PDFs a coral FileText,
// Word/Excel/CSV a navy FileText with a tiny "W"/"X"/"C" corner badge.
//
// The parent is polymorphic:
//   • { type: "element",     id: "<element_id>" }
//   • { type: "association", id: "<aid>" }
// A tiny module-level cache means multiple hovers over the same parent
// only fetch once per session.
import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { FileText, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CACHE = new Map(); // `${type}:${id}` → items[]

export function invalidateAttachmentHoverCache(type, id) {
  CACHE.delete(`${type}:${id}`);
}

export default function AttachmentHoverPreview({
  parent,          // { type: 'element'|'association', id: string }
  count = 0,       // when 0, no popover is opened
  side = "left",   // 'left' | 'right' | 'top' | 'bottom'
  wrapperClass = "relative inline-block",
  wrapperStyle,
  children,
  testid,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(CACHE.get(`${parent.type}:${parent.id}`) || null);
  const [loading, setLoading] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const hoverTimerRef = useRef(null);
  const wrapperRef = useRef(null);
  const popoverRef = useRef(null);

  const listUrl = parent.type === "association"
    ? `${API}/associations/${parent.id}/attachments`
    : `${API}/elements/${parent.id}/attachments`;

  const load = useCallback(async () => {
    if (items != null || loading || count === 0) return;
    setLoading(true);
    try {
      const r = await axios.get(listUrl, { withCredentials: true });
      const list = r.data || [];
      CACHE.set(`${parent.type}:${parent.id}`, list);
      setItems(list);
    } catch { /* non-blocking */ }
    finally { setLoading(false); }
  }, [listUrl, parent.type, parent.id, items, loading, count]);

  const onEnter = () => {
    if (count === 0) return;
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      setOpen(true);
      load();
    }, 120);
  };
  const onLeave = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setOpen(false);
  };

  // 2026-03-01 (Dave) — Portal the popover to <body> so it isn't clipped
  // by ancestors with `overflow: hidden` (the ControlsStrip container
  // rounds its own corners with overflow-hidden, which used to swallow
  // the popover). Position is `fixed` with coords derived from the
  // wrapper's bounding rect + configured `side`.
  useLayoutEffect(() => {
    if (!open || !wrapperRef.current) return;
    const r = wrapperRef.current.getBoundingClientRect();
    const width = 200;   // popover minWidth
    const gap = 8;
    const heightGuess = 240;
    let top = 0, left = 0;
    if (side === "left") {
      top = r.top + r.height / 2 - heightGuess / 2;
      left = r.left - width - gap;
    } else if (side === "right") {
      top = r.top + r.height / 2 - heightGuess / 2;
      left = r.right + gap;
    } else if (side === "top") {
      top = r.top - heightGuess - gap;
      left = r.left + r.width / 2 - width / 2;
    } else { // bottom
      top = r.bottom + gap;
      left = r.right - width; // right-align to trigger's right edge
    }
    // Clamp to viewport with an 8-px inset.
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (left + width > vw - 8) left = vw - width - 8;
    if (left < 8) left = 8;
    if (top + heightGuess > vh - 8) top = vh - heightGuess - 8;
    if (top < 8) top = 8;
    setCoords({ top, left });
  }, [open, side, items]);

  const popover = open && count > 0 ? (
    <div
      ref={popoverRef}
      className="fixed z-[9999] bg-white border-2 border-navy shadow-lg rounded-[6px] px-2 py-1.5"
      style={{ top: coords.top, left: coords.left, minWidth: 200 }}
      data-testid={`${testid || "attach-hover"}-popover`}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      {loading || items == null ? (
        <div className="flex items-center gap-1 text-[10px] text-slate py-1">
          <Loader2 size={10} className="animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="text-[10px] text-slate py-1">No attachments.</div>
      ) : (
        <ul className="flex flex-col gap-1.5 max-h-[220px] overflow-auto">
          {items.slice(0, 8).map((att) => {
            const lower = (att.filename || "").toLowerCase();
            const isImage = (att.content_type || "").startsWith("image/");
            const isPdf   = att.content_type === "application/pdf";
            const isWord  = att.content_type === "application/msword"
                         || att.content_type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                         || lower.endsWith(".doc") || lower.endsWith(".docx");
            const isExcel = att.content_type === "application/vnd.ms-excel"
                         || att.content_type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                         || lower.endsWith(".xls") || lower.endsWith(".xlsx");
            const isCsv   = att.content_type === "text/csv"
                         || att.content_type === "application/csv"
                         || lower.endsWith(".csv");
            const kindLabel = isImage ? "Image"
              : isPdf ? "PDF"
              : isWord ? "Word"
              : isExcel ? "Excel"
              : isCsv ? "CSV"
              : (att.content_type || "File");
            const tileColor = isPdf ? "text-coral"
              : (isWord || isExcel || isCsv) ? "text-navy"
              : "text-slate";
            return (
              <li
                key={att.attachment_id}
                className="flex items-center gap-2"
                title={att.filename}
              >
                {isImage ? (
                  <img
                    src={`${API}/attachments/${att.attachment_id}/download`}
                    alt=""
                    className="border border-rule rounded-[3px] object-cover"
                    style={{ width: 40, height: 40 }}
                    loading="lazy"
                  />
                ) : (
                  <div
                    className="border border-rule rounded-[3px] flex items-center justify-center bg-navy-wash/40 relative"
                    style={{ width: 40, height: 40 }}
                  >
                    <FileText size={18} className={tileColor} />
                    {(isWord || isExcel || isCsv) && (
                      <span
                        className="absolute bottom-0 right-0 px-1 text-[7px] font-bold rounded-tl-[3px] bg-white/95 text-navy"
                        style={{ lineHeight: 1.3 }}
                      >
                        {isWord ? "W" : isExcel ? "X" : "C"}
                      </span>
                    )}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-navy truncate max-w-[200px]">{att.filename}</div>
                  <div className="text-[9px] text-slate uppercase tracking-wider">
                    {kindLabel}
                  </div>
                </div>
              </li>
            );
          })}
          {items.length > 8 && (
            <li className="text-[10px] text-slate italic pt-0.5">
              + {items.length - 8} more — open the modal to see all.
            </li>
          )}
        </ul>
      )}
    </div>
  ) : null;

  return (
    <>
      <span
        ref={wrapperRef}
        className={wrapperClass}
        style={wrapperStyle}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        data-testid={testid}
      >
        {children}
      </span>
      {popover && typeof document !== "undefined"
        ? ReactDOM.createPortal(popover, document.body)
        : null}
    </>
  );
}
